import React, { useState, useEffect } from 'react';

import { Box, Typography, Card } from '@mui/material';

import { DynamicFormElOnChange } from '@/components/DynamicForm/types/DynamicFormTypes';

import SelectImageInput from './SelectImageInput';
import {
	imagePickerWithPreviewContainerStyles,
	getUploadAndReviewInfoContainerStyles,
	cardContainerStyles,
	imageContainerStyles,
	getImageStyles,
} from './ImagePickerWithPreview.styles';
export interface ImagePickerWithPreviewProps {
	id?: string;
	accept?: string;
	centerAlignInfo?: boolean;
	fileInfo?: string;
	reviewInfo?: string;
	uploadInfo?: string;
	selectedImageFile?: File | null;
	onBlur?: () => void;
	onChange?: DynamicFormElOnChange;
	showError?: boolean;
}
interface ImageFileTypes {
	'.png': string;
	'.jpg': string;
	'.jpeg': string;
}

const ImageFileTypesObj: ImageFileTypes = {
	'.png': 'image/png',
	'.jpg': 'image/jpg',
	'.jpeg': 'image/jpeg',
};

export default function ImagePickerWithPreview({
	id,
	centerAlignInfo,
	uploadInfo,
	reviewInfo,
	fileInfo,
	accept = '.png, .jpg, .jpeg',
	selectedImageFile,
	onBlur,
	onChange,
	showError = false,
}: Readonly<ImagePickerWithPreviewProps>) {
	const [file, setFile] = useState<File | null>(selectedImageFile ?? null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	const isInfoCenterAlign = centerAlignInfo || false;

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();

		if (!e.target.files) return;

		setFile(e.target.files[0]);

		onChange?.(e);
		onBlur?.();
	};

	const handleFileDrag = (e: React.DragEvent<HTMLDivElement>) => {
		if (previewUrl) return;

		e.preventDefault();
	};

	const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
		if (previewUrl) return;

		e.preventDefault();

		if (!e.dataTransfer.files) return;

		const imageFile = e.dataTransfer.files[0];

		const validImageTypes = accept
			.split(',')
			.map((acceptType) => ImageFileTypesObj[acceptType.trim() as keyof ImageFileTypes])
			.filter((acceptType) => acceptType);

		if (!validImageTypes.includes(imageFile.type)) {
			e.stopPropagation();
			e.preventDefault();
			return;
		}

		setFile(imageFile);

		onChange?.({ target: { files: [imageFile], name: id } } as unknown as React.ChangeEvent<HTMLInputElement>);
		onBlur?.();
	};

	useEffect(() => {
		if (!file) return;

		setPreviewUrl(URL.createObjectURL(file));

		return () => URL.revokeObjectURL(previewUrl as string);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [file]);

	return (
		<Box
			data-test-id="image-picker-with-preview"
			id={id ?? 'image-picker-with-preview'}
			sx={imagePickerWithPreviewContainerStyles}
		>
			<Box
				data-test-id={previewUrl ? 'review-upload-information' : 'helper-upload-information'}
				className={previewUrl ? 'review-upload-information' : 'helper-text-upload-information'}
				sx={getUploadAndReviewInfoContainerStyles(isInfoCenterAlign)}
			>
				<Typography
					data-test-id={previewUrl ? 'review-upload-information-text' : 'helper-upload-information-text'}
					variant="bodyMediumBook"
				>
					{previewUrl ? reviewInfo ?? 'Review your photo' : uploadInfo ?? 'Please upload a compatible photo'}
				</Typography>
			</Box>

			<Box data-test-id="file-information" className="file-information" display="flex" justifyContent="center">
				<Typography data-test-id="file-information-text" variant="bodyMediumBook">
					{fileInfo ?? 'PNG or JPG'}
				</Typography>
			</Box>

			<Card
				data-test-id="image-uploader-section"
				variant="outlined"
				raised={false}
				className={previewUrl ? 'section-image-preview' : 'section-image-upload'}
				onDragOver={(e) => handleFileDrag(e)}
				onDrop={(e) => handleFileDrop(e)}
				sx={{
					...cardContainerStyles,
					borderStyle: showError ? 'solid' : 'dashed',
					borderColor: showError ? 'error.dark' : 'neutrals.primary',
				}}
			>
				{previewUrl && (
					<Box
						data-test-id="image-preview-container"
						className="image-preview-container"
						sx={imageContainerStyles}
					>
						<Box data-test-id="image-preview" className="image" sx={getImageStyles(previewUrl)} />
					</Box>
				)}
				<SelectImageInput id={id} accept={accept} handleOnChange={handleOnChange} previewUrl={previewUrl} />
			</Card>
		</Box>
	);
}
